import React from 'react';
import { Box, Typography, Button, Card, CardContent, Alert, Stack, Divider, List, ListItem, ListItemIcon, ListItemText } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import { useLicense } from '../context/LicenseContext';
import LicenseActivation from './LicenseActivation';

export default function LicenseUpgrade() {
  const { licenseInfo, getRemainingDays, removeLicense, isLoading } = useLicense();
  const remainingDays = getRemainingDays();

  // Format date helper function
  const formatDate = (dateString) => {
    if (!dateString) return 'Not specified';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'Invalid Date';
      
      return date.toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (e) {
      console.error('Date parsing error:', e, dateString);
      return 'Invalid Date';
    }
  };

  // If no license is active, show activation component
  if (!licenseInfo) {
    return <LicenseActivation />;
  }

  const handleRemoveLicense = async () => {
    if (window.confirm('Are you sure you want to remove this license? You will need to reactivate to access developer features.')) {
      const result = await removeLicense();
      if (result.success) {
        window.location.reload();
      } else {
        alert(`Failed to remove license: ${result.error}`);
      }
    }
  };

  // Convert features object to array for display
  const getFeaturesList = () => {
    if (!licenseInfo?.features) return [];
    
    return Object.entries(licenseInfo.features).map(([featureKey, featureData]) => ({
      name: featureKey,
      enabled: featureData.enabled,
      details: getFeatureDetails(featureData)
    }));
  };

  // Get readable details from feature data
  const getFeatureDetails = (featureData) => {
    const details = [];
    if (featureData.max_requests_per_day) {
      details.push(`${featureData.max_requests_per_day} requests/day`);
    }
    if (featureData.max_tenants) {
      details.push(`${featureData.max_tenants} tenants`);
    }
    if (featureData.allowed_themes) {
      details.push(`Themes: ${featureData.allowed_themes.join(', ')}`);
    }
    if (featureData.data_retention_days) {
      details.push(`${featureData.data_retention_days} days retention`);
    }
    return details.join(', ');
  };

  return (
    <Box sx={{ p: 4 }}>
      {licenseInfo?.isInGracePeriod ? (
        <Alert severity="warning" sx={{ mb: 3 }}>
          License Expired - Grace Period: {remainingDays} days remaining
        </Alert>
      ) : licenseInfo?.type === 'evaluation' && (
        <Alert severity="info" sx={{ mb: 3 }}>
          Evaluation License - {remainingDays} days remaining
        </Alert>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">License Information</Typography>
        <Button 
          variant="outlined" 
          color="error"
          onClick={handleRemoveLicense}
          disabled={isLoading}
        >
          {isLoading ? 'Removing...' : 'Remove License'}
        </Button>
      </Box>

      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Stack spacing={2}>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">License Key</Typography>
              <Typography>{licenseInfo.current_license_key || 'N/A'}</Typography>
            </Box>
            
            <Box>
              <Typography variant="subtitle2" color="text.secondary">Type</Typography>
              <Typography sx={{ textTransform: 'capitalize' }}>
                {licenseInfo.type || 'N/A'}
              </Typography>
            </Box>
            
            <Box>
              <Typography variant="subtitle2" color="text.secondary">Customer</Typography>
              <Typography>{licenseInfo.customer || 'N/A'}</Typography>
            </Box>

            <Box>
              <Typography variant="subtitle2" color="text.secondary">Created</Typography>
              <Typography>{formatDate(licenseInfo.created_at)}</Typography>
            </Box>
            
            <Box>
              <Typography variant="subtitle2" color="text.secondary">Expires</Typography>
              <Typography>{formatDate(licenseInfo.expires_at)}</Typography>
            </Box>

            <Divider />
            
            {licenseInfo.restrictions && (
              <Box>
                <Typography variant="subtitle2" color="text.secondary">Restrictions</Typography>
                <List>
                  {Object.entries(licenseInfo.restrictions).map(([key, value]) => (
                    <ListItem key={key}>
                      <ListItemText 
                        primary={key.split('_').map(word => 
                          word.charAt(0).toUpperCase() + word.slice(1)
                        ).join(' ')}
                        secondary={Array.isArray(value) ? value.join(', ') : value}
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}

            {licenseInfo.features && (
              <Box>
                <Typography variant="subtitle2" color="text.secondary">Features</Typography>
                <List>
                  {getFeaturesList().map((feature) => (
                    <ListItem key={feature.name}>
                      <ListItemIcon>
                        {feature.enabled ? (
                          <CheckCircleIcon color="success" />
                        ) : (
                          <CancelIcon color="error" />
                        )}
                      </ListItemIcon>
                      <ListItemText 
                        primary={feature.name.split('_').map(word => 
                          word.charAt(0).toUpperCase() + word.slice(1)
                        ).join(' ')}
                        secondary={feature.details}
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}
          </Stack>
        </CardContent>
      </Card>

      <Typography variant="h5" gutterBottom sx={{ mt: 4 }}>
        Available Upgrades
      </Typography>

      <Box sx={{ display: 'flex', gap: 3, mt: 2 }}>
        <Card sx={{ flex: 1 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>Professional</Typography>
            <ul>
              <li>API Testing Tools</li>
              <li>Tenant Management</li>
              <li>Theme Customization</li>
              <li>Basic Integration Support</li>
            </ul>
            <Button 
              variant="contained" 
              color="primary"
              fullWidth
              sx={{ mt: 2 }}
              onClick={() => window.location.href = '/contact-sales'}
            >
              Contact Sales
            </Button>
          </CardContent>
        </Card>

        <Card sx={{ flex: 1 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>Enterprise</Typography>
            <ul>
              <li>All Professional Features</li>
              <li>Advanced Tenant Features</li>
              <li>Custom Integration Support</li>
              <li>Priority Support</li>
            </ul>
            <Button 
              variant="contained" 
              color="primary"
              fullWidth
              sx={{ mt: 2 }}
              onClick={() => window.location.href = '/contact-sales'}
            >
              Contact Sales
            </Button>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
} 